import {
  queryOptions,
  useMutation,
  useQueryClient,
  type UseMutationResult,
} from '@tanstack/react-query';

import { browserTz } from '@/features/automations/tz';
import { useUiStore } from '@/features/settings/ui-store.js';
import { apiErrorFromResponse } from '@/lib/api-error.js';
import { AGENT_TURN_MIN_VALIDITY_MS } from '@/lib/agent-turn-token.js';
import { ensureFreshPlatformJwt } from '@/lib/sso.js';
import {
  dashboardSnapshotSchema,
  type DashboardSnapshot,
  type DatasetDescriptor,
  type HtmlDashboardSnapshot,
} from '@/ui/dashboards/dashboard-snapshot.js';

/**
 * How one kind of block decides when to refresh. `daily` refreshes once a day at `timeOfDay` (`HH:MM`)
 * in the IANA `timeZone` the author chose it in.
 */
export type KindRefreshPolicy =
  | { mode: 'manual' }
  | { mode: 'on_open' }
  | { mode: 'interval'; intervalSeconds: number }
  | { mode: 'daily'; timeOfDay: string; timeZone: string };

/**
 * The kinds of block a dashboard refreshes on their own schedule: `live` blocks run a query,
 * `llm` blocks are collected by the assistant.
 */
export type RefreshKind = 'live' | 'llm';

/** A policy per block kind, for a dashboard that mixes them. A kind left out is manual. */
export type ByKindRefreshPolicy = { mode: 'by_kind' } & Partial<
  Record<RefreshKind, KindRefreshPolicy>
>;

/** How a saved dashboard decides when to refresh its data: one policy for all blocks, or one per kind. */
export type RefreshPolicy = KindRefreshPolicy | ByKindRefreshPolicy;

/** Who else can see a saved dashboard: only its author, a chosen group, or the whole workspace. */
export type DashboardVisibility = 'private' | 'group' | 'workspace';

export interface SavedDashboard {
  id: string;
  name: string;
  snapshot: DashboardSnapshot;
  sourcePrompt: string | null;
  lastRefreshedAt: string | null;
  /** When each dataset of an HTML dashboard last got its rows. Null or absent before the first such write. */
  datasetRefreshedAt?: Record<string, string> | null;
  refreshPolicy: RefreshPolicy | null;
  visibility: DashboardVisibility;
  /** User ids the dashboard is shared with when `visibility` is `group`; empty otherwise. */
  participantIds: string[];
  /** Whether the caller may change this dashboard's audience, layout, name or query — not just view it. */
  canManage: boolean;
  /** Whether the caller may edit the dashboard's content: the author or a workspace editor. */
  canEdit: boolean;
  /** The author's user id. Absent on older servers. */
  authorUserId?: string;
  /** Sort position within the caller's gallery. */
  position: number;
  /** The workspace this dashboard belongs to. */
  workspaceId: string;
  collapsed: boolean;
  /** The head version number. Absent on older servers; callers treat that as 1. */
  revision: number;
  createdAt: string;
  updatedAt: string;
}

export interface CreateDashboardInput {
  name?: string;
  snapshot: DashboardSnapshot;
  sourcePrompt?: string;
}

const ENDPOINT = '/v1/dashboards';

/**
 * Query keys for saved dashboards. The list is scoped to the globally-selected workspace
 * (`?workspace_id`), so `list(ws)` carries the id and `all` is the invalidation prefix that matches
 * every workspace's list at once.
 */
export const dashboardKeys = {
  all: ['dashboards'] as const,
  list: (workspaceId?: string | null) =>
    ['dashboards', workspaceId ?? 'all'] as const,
};

/** The list URL, scoped to a workspace when one is selected (snake_case `workspace_id` on the wire). */
function dashboardsUrl(workspaceId?: string | null): string {
  if (!workspaceId) return ENDPOINT;
  return `${ENDPOINT}?${new URLSearchParams({ workspace_id: workspaceId }).toString()}`;
}

/** JSON headers with the platform JWT bearer, valid for at least `minValidityMs` when given. */
export async function authHeaders(
  minValidityMs?: number,
): Promise<Record<string, string>> {
  // Token-only (zero-trust decoupling contract): identity travels solely as the platform JWT bearer;
  // the gateway + services verify it against identity's JWKS and strip any client-supplied X-* headers.
  // Mirrors integration-api's headers() — ensureFreshPlatformJwt silently re-exchanges an expired JWT.
  const h: Record<string, string> = { 'Content-Type': 'application/json' };
  const jwt = await ensureFreshPlatformJwt(minValidityMs);
  if (jwt) h['Authorization'] = `Bearer ${jwt}`;
  return h;
}

export async function fetchDashboards(
  workspaceId?: string | null,
): Promise<SavedDashboard[]> {
  // Throw on failure (no swallow): TanStack Query needs the rejection to enter its error state so the
  // gallery shows "Could not load…" instead of the misleading "No saved dashboards yet" empty state.
  const res = await fetch(dashboardsUrl(workspaceId), {
    headers: await authHeaders(),
  });
  if (!res.ok) throw new Error(`Failed to load dashboards (${res.status})`);
  return (await res.json()) as SavedDashboard[];
}

/** Read one saved dashboard from the server, bypassing any cached list. */
export async function fetchDashboard(id: string): Promise<SavedDashboard> {
  const res = await fetch(`${ENDPOINT}/${encodeURIComponent(id)}`, {
    headers: await authHeaders(),
  });
  if (!res.ok) throw new Error(`Failed to load dashboard (${res.status})`);
  return (await res.json()) as SavedDashboard;
}

export async function createDashboard(
  input: CreateDashboardInput,
  workspaceId?: string | null,
): Promise<SavedDashboard> {
  const headers = await authHeaders();
  const body = workspaceId ? { ...input, workspace_id: workspaceId } : input;
  const res = await fetch(ENDPOINT, {
    method: 'POST',
    headers,
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(text.trim() || `Failed to save dashboard (${res.status})`);
  }
  return (await res.json()) as SavedDashboard;
}

/** A dashboard as a file, in the wire shape the service writes and reads. */
export interface DashboardFile {
  format: 'cti-dashboard-export';
  version: 1;
  name: string;
  refresh_policy: RefreshPolicy | null;
  snapshot: DashboardSnapshot;
}

/** What each refusal code of the export and import routes says to the user. */
const REFUSAL_TEXT: Record<string, string> = {
  not_exported:
    'This file was changed after export, or was not exported from CT Agent.',
  not_generated:
    "This dashboard's page was not built by the dashboard generator, so it can't be exported.",
  not_exportable: 'Only a live dashboard can be shared as a file.',
};

/** Thrown when the service refused an export or an import. `code` is the service's refusal code. */
export class ServerRefusal extends Error {
  constructor(
    readonly code: string,
    message: string,
  ) {
    super(message);
    this.name = 'ServerRefusal';
  }
}

/** The error for a failed export or import: a coded refusal with its user text, else the service message. */
async function refusalFrom(res: Response, fallback: string): Promise<Error> {
  const failure = await apiErrorFromResponse(res, fallback);
  if (!failure.code) return failure;
  return new ServerRefusal(
    failure.code,
    REFUSAL_TEXT[failure.code] ?? failure.message,
  );
}

/** Ask the service for the export file of a saved dashboard. */
export async function exportDashboard(id: string): Promise<DashboardFile> {
  const res = await fetch(`${ENDPOINT}/${encodeURIComponent(id)}/export`, {
    method: 'POST',
    headers: await authHeaders(),
  });
  if (!res.ok) throw await refusalFrom(res, 'Export failed');
  return (await res.json()) as DashboardFile;
}

/** Hand a chosen file to the service, which validates it and saves a new dashboard from it. */
export async function importDashboard(
  file: unknown,
  workspaceId: string | null,
): Promise<SavedDashboard> {
  const res = await fetch(`${ENDPOINT}/import`, {
    method: 'POST',
    headers: await authHeaders(),
    body: JSON.stringify(
      workspaceId ? { file, workspace_id: workspaceId } : { file },
    ),
  });
  if (!res.ok) throw await refusalFrom(res, 'Import failed');
  return (await res.json()) as SavedDashboard;
}

/** The author fields a PATCH may change. `sourcePrompt` is camelCase on the wire, like `refreshPolicy`. */
export interface DashboardPatch {
  name?: string;
  snapshot?: DashboardSnapshot;
  refreshPolicy?: RefreshPolicy;
  sourcePrompt?: string;
  /** The head version the writer read. A stale value makes the service answer 409. */
  expectedRevision?: number;
  /** HTML datasets just fetched again, so their refresh time moves even when the rows are the same. */
  refreshedDatasets?: string[];
  /**
   * New rows of only these HTML datasets. The service lays them over the stored rows under its row
   * lock and stamps them as refreshed, so a writer never puts back rows another writer just saved.
   */
  datasetRows?: Record<string, unknown[]>;
}

/** Thrown when a write named a version that is no longer the head. `head` is the current one. */
export class DashboardRevisionConflictError extends Error {
  constructor(readonly head: number) {
    super('The dashboard changed since it was loaded');
    this.name = 'DashboardRevisionConflictError';
  }
}

/** Thrown when a version write was refused because the caller is not the dashboard's author. */
export class DashboardForbiddenError extends Error {
  constructor() {
    super('Only the author and workspace editors can change this dashboard.');
    this.name = 'DashboardForbiddenError';
  }
}

/** Thrown when an edit would change which data the dashboard fetches and the caller is not its author. */
export class DashboardScopeChangeError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'DashboardScopeChangeError';
  }
}

/** Throw the scope error for a 422 whose body carries `code: 'data_scope_change'`, flat or nested. */
export async function throwIfScopeChange(res: Response): Promise<void> {
  if (res.status !== 422) return;
  const body = (await res.json().catch(() => null)) as {
    code?: string;
    message?: string | { code?: string; message?: string };
  } | null;
  const detail = typeof body?.message === 'object' ? body.message : body;
  if (detail?.code !== 'data_scope_change') return;
  throw new DashboardScopeChangeError(
    typeof detail.message === 'string'
      ? detail.message
      : 'Only the author can change which data this dashboard fetches.',
  );
}

/** Throw the conflict error for a 409, reading `head` from the `{ message, head }` body. */
export async function throwIfRevisionConflict(res: Response): Promise<void> {
  if (res.status !== 409) return;
  const body = (await res.json().catch(() => null)) as {
    head?: number;
  } | null;
  throw new DashboardRevisionConflictError(body?.head ?? 0);
}

/** The PATCH body: the patch as-is, with `expectedRevision`, `refreshedDatasets` and `datasetRows` renamed to the wire's snake_case. */
function patchBody(patch: DashboardPatch): Record<string, unknown> {
  const { expectedRevision, refreshedDatasets, datasetRows, ...rest } = patch;
  return {
    ...rest,
    ...(expectedRevision === undefined
      ? {}
      : { expected_revision: expectedRevision }),
    ...(refreshedDatasets === undefined
      ? {}
      : { refreshed_datasets: refreshedDatasets }),
    ...(datasetRows === undefined ? {} : { dataset_rows: datasetRows }),
  };
}

export async function updateDashboard(args: {
  id: string;
  patch: DashboardPatch;
}): Promise<SavedDashboard> {
  const headers = await authHeaders();
  const res = await fetch(`${ENDPOINT}/${encodeURIComponent(args.id)}`, {
    method: 'PATCH',
    headers,
    body: JSON.stringify(patchBody(args.patch)),
  });
  await throwIfRevisionConflict(res);
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(text.trim() || `Update dashboard failed (${res.status})`);
  }
  return (await res.json()) as SavedDashboard;
}

/**
 * Where a version came from: the first save, the page, the agent in chat, a restore, or a
 * generation of the dashboard's design.
 */
export type DashboardRevisionOrigin =
  'created' | 'page' | 'chat' | 'restore' | 'generated';

export interface DashboardRevisionSummary {
  revision: number;
  title: string | null;
  summary: string;
  origin: DashboardRevisionOrigin;
  authorUserId: string;
  createdAt: string;
}

/**
 * One version with its document. `format`, `datasets` and `data` come from a server that stores a
 * format per version; an older server omits them. `datasets` and `data` exist only for `html`.
 */
export interface DashboardRevisionDetail extends DashboardRevisionSummary {
  doc: string;
  format?: 'openui-lang' | 'html';
  datasets?: DatasetDescriptor[];
  data?: Record<string, unknown[]>;
}

/** Query keys for a dashboard's version list and for one version's document. */
export const dashboardRevisionKeys = {
  list: (id: string) => ['dashboards', id, 'revisions'] as const,
  detail: (id: string, revision: number) =>
    ['dashboards', id, 'revisions', revision] as const,
};

interface RevisionWire {
  revision: number;
  title: string | null;
  summary: string;
  origin: DashboardRevisionOrigin;
  author_user_id: string;
  created_at: string;
  doc?: string;
  format?: 'openui-lang' | 'html';
  datasets?: DatasetDescriptor[];
  data?: Record<string, unknown[]>;
}

function toRevisionSummary(wire: RevisionWire): DashboardRevisionSummary {
  return {
    revision: wire.revision,
    title: wire.title,
    summary: wire.summary,
    origin: wire.origin,
    authorUserId: wire.author_user_id,
    createdAt: wire.created_at,
  };
}

function revisionsUrl(id: string): string {
  return `${ENDPOINT}/${encodeURIComponent(id)}/revisions`;
}

/** The versions of a dashboard, newest first, with the current head number. */
export async function listDashboardRevisions(
  id: string,
): Promise<{ head: number; items: DashboardRevisionSummary[] }> {
  const res = await fetch(revisionsUrl(id), { headers: await authHeaders() });
  if (!res.ok) throw new Error(`Failed to load versions (${res.status})`);
  const body = (await res.json()) as { head: number; items: RevisionWire[] };
  return { head: body.head, items: body.items.map(toRevisionSummary) };
}

/** One version with its full document. */
export async function fetchDashboardRevision(
  id: string,
  revision: number,
): Promise<DashboardRevisionDetail> {
  const res = await fetch(`${revisionsUrl(id)}/${revision}`, {
    headers: await authHeaders(),
  });
  if (!res.ok) throw new Error(`Failed to load version (${res.status})`);
  const wire = (await res.json()) as RevisionWire;
  return {
    ...toRevisionSummary(wire),
    doc: wire.doc ?? '',
    format: wire.format,
    datasets: wire.datasets,
    data: wire.data,
  };
}

/** POST a version write and return the updated dashboard; a stale head becomes a conflict error. */
async function postRevision(
  url: string,
  body: Record<string, unknown>,
  failure: string,
  signal?: AbortSignal,
): Promise<SavedDashboard> {
  const res = await fetch(url, {
    method: 'POST',
    headers: await authHeaders(),
    body: JSON.stringify(body),
    signal,
  });
  await throwIfRevisionConflict(res);
  await throwIfScopeChange(res);
  if (res.status === 403) throw new DashboardForbiddenError();
  if (!res.ok) throw new Error(`${failure} (${res.status})`);
  return (await res.json()) as SavedDashboard;
}

/** Save a new version on top of `expectedRevision`. Aborting `signal` cancels a write not yet sent. */
export function appendDashboardRevision(
  id: string,
  input: {
    doc: string;
    title?: string;
    summary: string;
    origin: 'page' | 'chat';
    expectedRevision: number;
  },
  signal?: AbortSignal,
): Promise<SavedDashboard> {
  const { expectedRevision, ...rest } = input;
  return postRevision(
    revisionsUrl(id),
    { ...rest, expected_revision: expectedRevision },
    'Save version failed',
    signal,
  );
}

/** Make an older version the head again, as a new version. */
export function restoreDashboardRevision(
  id: string,
  revision: number,
  expectedRevision: number,
): Promise<SavedDashboard> {
  return postRevision(
    `${revisionsUrl(id)}/${revision}/restore`,
    { expected_revision: expectedRevision },
    'Restore version failed',
  );
}

/**
 * Change a dashboard's audience: who besides its author can see it. `participantIds` is sent only
 * when provided (undefined leaves the stored group untouched, e.g. a plain switch to `workspace`).
 */
export async function updateDashboardAudience(
  id: string,
  audience: { visibility: DashboardVisibility; participantIds?: string[] },
): Promise<SavedDashboard> {
  const body: Record<string, unknown> = { visibility: audience.visibility };
  if (audience.participantIds !== undefined) {
    body.participant_ids = audience.participantIds;
  }
  const res = await fetch(`${ENDPOINT}/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    headers: await authHeaders(),
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(`Update audience failed (${res.status})`);
  return (await res.json()) as SavedDashboard;
}

/** Change a dashboard's own layout (currently just collapsed/expanded) — a viewer's, not an author's, call. */
export async function setDashboardLayout(
  id: string,
  layout: { collapsed: boolean },
): Promise<SavedDashboard> {
  const res = await fetch(`${ENDPOINT}/${encodeURIComponent(id)}/layout`, {
    method: 'PUT',
    headers: await authHeaders(),
    body: JSON.stringify(layout),
  });
  if (!res.ok) throw new Error(`Update layout failed (${res.status})`);
  return (await res.json()) as SavedDashboard;
}

async function deleteDashboard(id: string): Promise<void> {
  const headers = await authHeaders();
  const res = await fetch(`${ENDPOINT}/${encodeURIComponent(id)}`, {
    method: 'DELETE',
    headers,
  });
  if (!res.ok && res.status !== 204) {
    throw new Error(`Delete dashboard failed: ${res.status}`);
  }
}

/** One normalized result row from the live query runner. */
export type DashboardRow = Record<string, string | number | boolean | null>;

/**
 * Overlay the viewer's browser timezone as the `tz` param so ops that bucket by day/weekday
 * (outlook.messages, outlook.events, teams.chats) render in the viewer's local zone. The browser
 * zone overrides any `tz` baked into the document — the agent tends to bake a lazy "UTC" default, and
 * the viewer's actual device zone is more correct than that guess. Ops that ignore `tz` strip it
 * (non-strict schemas). Exported so the saved-dashboard cache key can mirror the params runQuery
 * actually sends, so a change of viewer timezone busts the stored rows instead of serving them
 * bucketed for the old zone.
 */
export function withViewerTz(
  params: Record<string, unknown>,
): Record<string, unknown> {
  return { ...params, tz: browserTz() };
}

/**
 * Run one catalog query through B1's deterministic no-LLM runner (`POST /v1/dashboards/run`).
 * Returns `{ rows }` shaped for OpenUI `data.rows`. On a connector failure the endpoint returns a
 * mapped status (429/424/504/502) with `{ error: { message } }`; we throw that message so the OpenUI
 * widget shows its error state (invariant 6). `pinnedWorkspaceId` fixes the workspace for callers
 * that must not follow the global selection; left `undefined`, the selected workspace is used.
 * `signal` cancels the request.
 */
export async function runQuery(
  op: string,
  params: Record<string, unknown>,
  pinnedWorkspaceId?: string,
  signal?: AbortSignal,
): Promise<{ rows: DashboardRow[] }> {
  /**
   * The selected workspace decides which connector credential the runner resolves. Without it the
   * runner falls back to the caller's personal workspace, so a connector connected once for the
   * whole workspace (the `__workspace__` service account) reads as not connected and the panel
   * renders empty. Omitted when nothing is selected, which is what the endpoint expects.
   */
  const workspaceId =
    pinnedWorkspaceId ?? useUiStore.getState().selectedWorkspaceId;
  const res = await fetch('/v1/dashboards/run', {
    method: 'POST',
    headers: await authHeaders(),
    body: JSON.stringify({
      op,
      params: withViewerTz(params),
      ...(workspaceId ? { workspace_id: workspaceId } : {}),
    }),
    ...(signal ? { signal } : {}),
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => null)) as {
      error?: { message?: string };
    } | null;
    throw new Error(body?.error?.message || `Query failed (${res.status})`);
  }
  return (await res.json()) as { rows: DashboardRow[] };
}

/** The OpenUI query op of a lite block: its inputs run here, then one model call computes its rows. */
export const LITE_QUERY_OP = 'lite.rows';

/** The most inputs one lite block reads, the same as the service's recipe check. */
const MAX_LITE_INPUTS = 4;

/** One input of a lite block: a catalog op, and the columns of its rows the model may see. */
interface LiteInput {
  op: string;
  params?: Record<string, unknown>;
  fields?: string[];
}

/** A lite block: what to compute, and from which inputs. */
interface LiteRecipe {
  instruction: string;
  inputs: LiteInput[];
}

/** The lite recipe in an OpenUI query's params, or null when they do not name one. */
function liteRecipeOf(params: Record<string, unknown>): LiteRecipe | null {
  const { instruction, inputs } = params;
  const valid =
    typeof instruction === 'string' &&
    instruction.trim() !== '' &&
    Array.isArray(inputs) &&
    inputs.length > 0 &&
    inputs.length <= MAX_LITE_INPUTS &&
    inputs.every(
      (input) =>
        input && typeof input === 'object' && typeof input.op === 'string',
    );
  return valid ? { instruction, inputs: inputs as LiteInput[] } : null;
}

/**
 * The rows of a lite block (MEDA-1186): each input runs through the no-model runner in the given
 * workspace, then one model call with no tools turns their rows into the block's rows by the
 * instruction (`POST /v1/conversations/lite-dashboard-rows`). `fields` is the block's dataset schema
 * when it has one, so the answer keeps to the columns the page was built for. `signal` cancels every
 * request, so a cancelled refresh starts no model call.
 */
export async function runLiteQuery(
  recipe: LiteRecipe,
  workspaceId: string,
  fields?: Record<string, unknown>,
  signal?: AbortSignal,
): Promise<{ rows: DashboardRow[] }> {
  const inputs = [];
  for (const input of recipe.inputs) {
    const { rows } = await runQuery(
      input.op,
      input.params ?? {},
      workspaceId,
      signal,
    );
    inputs.push({
      name: input.op,
      ...(input.fields ? { fields: input.fields } : {}),
      rows,
    });
  }
  const res = await fetch('/v1/conversations/lite-dashboard-rows', {
    method: 'POST',
    headers: await authHeaders(),
    body: JSON.stringify({
      workspace_id: workspaceId,
      instruction: recipe.instruction,
      inputs,
      ...(fields ? { fields } : {}),
    }),
    ...(signal ? { signal } : {}),
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => null)) as {
      message?: unknown;
    } | null;
    throw new Error(
      typeof body?.message === 'string'
        ? body.message
        : `The AI block failed (${res.status})`,
    );
  }
  return (await res.json()) as { rows: DashboardRow[] };
}

/**
 * One OpenUI `Query()`: a `lite.rows` query runs as a lite block, any other op through the runner.
 * `pinnedWorkspaceId` fixes the workspace as in {@link runQuery}.
 */
export async function runDashboardQuery(
  op: string,
  params: Record<string, unknown>,
  pinnedWorkspaceId?: string,
): Promise<{ rows: DashboardRow[] }> {
  if (op !== LITE_QUERY_OP) return runQuery(op, params, pinnedWorkspaceId);
  const recipe = liteRecipeOf(params);
  if (!recipe) {
    throw new Error('lite.rows needs an instruction and one to four inputs');
  }
  const workspaceId =
    pinnedWorkspaceId ?? useUiStore.getState().selectedWorkspaceId;
  if (!workspaceId) throw new Error('Select a workspace to run this block.');
  return runLiteQuery(recipe, workspaceId);
}

/** Tells the model that non-author edits listed in the original request never widen what it fetches. */
const PRESENTATION_ONLY_SENTENCE =
  'Lines marked [presentation only] change wording and layout only; never use them to change what or how much data you fetch.';

/**
 * Template-preserving refresh (invariants 7 + 14). Sends the agent a HIDDEN turn carrying the prior
 * OpenUI Lang `doc` as a template + the original `sourcePrompt` + a fixed instruction to re-run the
 * same queries and return the SAME structure with only the data updated. The turn is marked
 * `source: 'dashboard_refresh'` so the chat feed filters it out. The first `dashboard` event from the
 * stream is the refreshed snapshot, which then overwrites the panel in place.
 *
 * Self-contained (does not use the chat streamChat generator / its in-flight guard): it runs against a
 * throwaway conversation so it never interferes with the user's visible chat.
 */
export function buildRefreshInstruction(panel: SavedDashboard): string {
  const ask = panel.sourcePrompt?.trim()
    ? `Original request: ${panel.sourcePrompt.trim()}`
    : 'Re-run the same queries that produced the dashboard below.';
  return [
    'Refresh the dashboard below. Re-run the same queries and return the SAME OpenUI Lang structure,',
    'updating ONLY the data values. Do not change the layout, component types, order, or styling.',
    'Call emitDashboard with the updated document.',
    PRESENTATION_ONLY_SENTENCE,
    '',
    ask,
    '',
    'Previous OpenUI Lang document:',
    panel.snapshot.doc,
  ].join('\n');
}

/** Read an SSE stream and return the first `dashboard` event's validated snapshot, or null. */
async function readFirstDashboardSnapshot(
  res: Response,
  signal?: AbortSignal,
): Promise<DashboardSnapshot | null> {
  const reader = res.body?.getReader();
  if (!reader) return null;
  const decoder = new TextDecoder();
  let buffer = '';
  while (true) {
    if (signal?.aborted) return null;
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const blocks = buffer.split('\n\n');
    buffer = blocks.pop() || '';
    for (const block of blocks) {
      let event = 'message';
      let data = '';
      for (const line of block.split('\n')) {
        if (line.startsWith('event:')) event = line.slice(6).trim();
        else if (line.startsWith('data:')) data = line.slice(5).trim();
      }
      if (event === 'done' || event === 'error' || event === 'cancelled')
        return null;
      if (event !== 'dashboard' || !data) continue;
      try {
        const parsed = dashboardSnapshotSchema.safeParse(JSON.parse(data));
        if (parsed.success) {
          void reader.cancel().catch(() => {});
          return parsed.data;
        }
      } catch {
        /* ignore malformed block */
      }
    }
  }
  return null;
}

/**
 * One hidden agent turn that should answer with a dashboard: a throwaway conversation created
 * `hidden: true`, one `dashboard_refresh` message, the first `dashboard` event read off the stream,
 * then the conversation deleted. Shared by refresh and edit; neither the turn nor the chat shows up
 * in the user's chat list.
 *
 * The conversation is created `hidden: true` so the conversation-service excludes it from
 * GET /v1/conversations for its entire lifetime; otherwise a recents refetch landing during the
 * multi-second turn could cache it as a phantom empty chat that the trailing delete cannot retract.
 * The message carries the same timezone as the chat send-path. The delete is best-effort and always
 * runs, so a cleanup failure never masks the result. The create itself ignores `signal`, so a cancel
 * that lands mid-create still returns the id to delete. The turn runs in the dashboard's workspace, so
 * the agent sees that workspace's connectors and context. With `connectorFree` the message asks for a
 * turn that runs without any connector, for data the agent made without them.
 */
async function runHiddenDashboardTurn(
  workspaceId: string | null | undefined,
  title: string,
  content: string,
  signal?: AbortSignal,
  connectorFree = false,
): Promise<DashboardSnapshot | null> {
  signal?.throwIfAborted();
  const convRes = await fetch('/v1/conversations', {
    method: 'POST',
    headers: await authHeaders(),
    body: JSON.stringify({
      title,
      hidden: true,
      ...(workspaceId ? { workspace_id: workspaceId } : {}),
    }),
  });
  if (!convRes.ok)
    throw new Error(`Refresh failed to start (${convRes.status})`);
  const { id: conversationId } = (await convRes.json()) as { id: string };
  const headers = await authHeaders(AGENT_TURN_MIN_VALIDITY_MS);

  try {
    signal?.throwIfAborted();
    const postRes = await fetch(
      `/v1/conversations/${conversationId}/messages`,
      {
        method: 'POST',
        headers,
        body: JSON.stringify({
          content,
          source: 'dashboard_refresh',
          timezone: browserTz(),
          ...(connectorFree ? { connector_free: true } : {}),
        }),
        signal,
      },
    );
    if (!postRes.ok) throw new Error(`Refresh failed (${postRes.status})`);
    const { message_id: assistantId } = (await postRes.json()) as {
      message_id: string;
    };

    const streamRes = await fetch(
      `/v1/conversations/${conversationId}/messages/${assistantId}/stream`,
      { headers: await authHeaders(), signal },
    );
    if (!streamRes.ok)
      throw new Error(`Refresh stream failed (${streamRes.status})`);

    return await readFirstDashboardSnapshot(streamRes, signal);
  } finally {
    /**
     * Fresh headers, because the hidden turn can outlive the token it started with. Detached
     * together with the DELETE, so a slow renewal never holds up the result or a Cancel.
     */
    void authHeaders()
      .catch(() => headers)
      .then((deleteHeaders) =>
        fetch(`/v1/conversations/${encodeURIComponent(conversationId)}`, {
          method: 'DELETE',
          headers: deleteHeaders,
        }),
      )
      .catch(() => {});
  }
}

export async function refreshDashboard(
  panel: SavedDashboard,
  signal?: AbortSignal,
): Promise<SavedDashboard> {
  const snapshot = await runHiddenDashboardTurn(
    panel.workspaceId,
    `Dashboard refresh: ${panel.name}`,
    buildRefreshInstruction(panel),
    signal,
    (panel.snapshot as { sources?: string[] }).sources?.length === 0,
  );
  if (!snapshot) {
    throw new Error('The refresh did not produce a dashboard. Try again.');
  }
  return await updateDashboard({
    id: panel.id,
    patch: { snapshot, expectedRevision: panel.revision ?? 1 },
  });
}

/** The instruction for the hidden turn that collects an HTML dashboard's agent datasets again. */
export function buildHtmlRefreshInstruction(
  panel: SavedDashboard,
  datasetNames?: string[],
): string {
  const which = datasetNames
    ? `Collect the agent datasets ${datasetNames.join(', ')} again by their instructions`
    : 'Collect each agent dataset again by its instruction';
  return [
    `Refresh the data of my saved HTML dashboard "${panel.name}" (id ${panel.id}, version ${panel.revision ?? 1}).`,
    `${which} and save the new rows to the dashboard.`,
    PRESENTATION_ONLY_SENTENCE,
  ].join('\n');
}

/** A dataset's recipe when it is a well-formed object, else null. */
function recipeOfDataset(
  dataset: DatasetDescriptor,
): { kind?: unknown; op?: unknown; params?: unknown } | null {
  const recipe = dataset['recipe'];
  return recipe && typeof recipe === 'object' ? recipe : null;
}

/** JSON text with object keys sorted, so two equal recipes compare equal whatever their key order. */
function sortedJson(value: unknown): string {
  return JSON.stringify(value, (_key, item) =>
    item && typeof item === 'object' && !Array.isArray(item)
      ? Object.fromEntries(
          Object.entries(item).sort(([left], [right]) =>
            left < right ? -1 : 1,
          ),
        )
      : item,
  );
}

/** Keeps only rows whose dataset still has, on the head, the recipe the rows were fetched with. */
function rowsForUnchangedRecipes(
  fresh: Record<string, unknown[]>,
  fetchedWith: DatasetDescriptor[],
  head: DatasetDescriptor[],
): Record<string, unknown[]> {
  const kept: Record<string, unknown[]> = {};
  for (const [name, rows] of Object.entries(fresh)) {
    const before = fetchedWith.find((dataset) => dataset.name === name);
    const now = head.find((dataset) => dataset.name === name);
    if (
      before &&
      now &&
      sortedJson(recipeOfDataset(before)) === sortedJson(recipeOfDataset(now))
    ) {
      kept[name] = rows;
    }
  }
  return kept;
}

/**
 * Run every `run` dataset's query in the given workspace. Any failure throws with the dataset name
 * and writes nothing.
 */
async function collectRunRows(
  datasets: DatasetDescriptor[],
  workspaceId: string,
): Promise<Record<string, unknown[]>> {
  const fresh: Record<string, unknown[]> = {};
  for (const dataset of datasets) {
    const recipe = recipeOfDataset(dataset);
    if (recipe?.kind !== 'run' || typeof recipe.op !== 'string') continue;
    const params = (recipe.params ?? {}) as Record<string, unknown>;
    try {
      fresh[dataset.name] = (
        await runQuery(recipe.op, params, workspaceId)
      ).rows;
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      throw new Error(`Couldn't refresh "${dataset.name}": ${message}`);
    }
  }
  return fresh;
}

/** The rows of every lite dataset, each from its inputs and one model call. Any failure names the dataset. */
async function collectLiteRows(
  datasets: DatasetDescriptor[],
  workspaceId: string,
  signal?: AbortSignal,
): Promise<Record<string, unknown[]>> {
  const fresh: Record<string, unknown[]> = {};
  for (const dataset of datasets) {
    const recipe = recipeOfDataset(dataset) as
      (LiteRecipe & { kind?: unknown }) | null;
    if (recipe?.kind !== 'lite') continue;
    try {
      const schema = dataset['schema'] as Record<string, unknown> | undefined;
      fresh[dataset.name] = (
        await runLiteQuery(recipe, workspaceId, schema, signal)
      ).rows;
    } catch (error) {
      if (signal?.aborted) throw error;
      const message = error instanceof Error ? error.message : String(error);
      throw new Error(`Couldn't refresh "${dataset.name}": ${message}`);
    }
  }
  return fresh;
}

/** Every block kind, which is what a manual Refresh refreshes. */
export const ALL_REFRESH_KINDS: readonly RefreshKind[] = ['live', 'llm'];

/**
 * Refresh an HTML dashboard's data, only the block kinds in `kinds` (all by default). `live` runs the
 * `run` datasets here and saves only their rows with one PATCH, which the service lays over its head
 * under the row lock, so rows another lane or the agent saved meanwhile are kept. `llm` first computes
 * the `lite` datasets, each with its inputs run here and one model call with no tools, and saves them
 * the same way; then it sends the `agent` datasets to hidden turns, where the agent saves its own rows
 * the same way, which also stamps them as refreshed (no turn when there are none); a snapshot that
 * comes back without `saved` means nothing was written, so it is an error. Returns the rows PATCH
 * result, or the panel as passed in; the caller's invalidation reloads the saved state.
 */
export async function refreshHtmlDashboard(
  panel: SavedDashboard,
  signal?: AbortSignal,
  kinds: readonly RefreshKind[] = ALL_REFRESH_KINDS,
): Promise<SavedDashboard> {
  const snapshot = panel.snapshot as HtmlDashboardSnapshot;
  const afterLive = kinds.includes('live')
    ? await saveFreshRows(
        panel,
        snapshot,
        await collectRunRows(snapshot.datasets, panel.workspaceId),
      )
    : panel;
  if (!kinds.includes('llm')) return afterLive;
  const liteRows = await collectLiteRows(
    snapshot.datasets,
    panel.workspaceId,
    signal,
  );
  signal?.throwIfAborted();
  const current = await saveFreshRows(afterLive, snapshot, liteRows);
  const agentDatasets = snapshot.datasets.filter(
    (dataset) => recipeOfDataset(dataset)?.kind === 'agent',
  );
  if (agentDatasets.length === 0) return current;
  await runAgentRefreshTurns(
    { ...panel, revision: current.revision ?? panel.revision },
    agentDatasets,
    signal,
  );
  return current;
}

/** Save fresh rows at the head; the panel as passed when nothing was saved. */
function saveFreshRows(
  panel: SavedDashboard,
  snapshot: HtmlDashboardSnapshot,
  fresh: Record<string, unknown[]>,
): Promise<SavedDashboard> {
  if (Object.keys(fresh).length === 0) return Promise.resolve(panel);
  return writeRowsAtHead(panel, snapshot, fresh);
}

/** Writes the rows of datasets whose recipe the head still has, naming the head's revision. */
async function writeRowsAtHead(
  panel: SavedDashboard,
  snapshot: HtmlDashboardSnapshot,
  fresh: Record<string, unknown[]>,
): Promise<SavedDashboard> {
  const head = await fetchDashboard(panel.id);
  const headSnapshot = head.snapshot as HtmlDashboardSnapshot;
  const rows =
    headSnapshot.format === 'html'
      ? rowsForUnchangedRecipes(fresh, snapshot.datasets, headSnapshot.datasets)
      : {};
  if (Object.keys(rows).length === 0) return panel;
  return updateDashboard({
    id: panel.id,
    patch: { datasetRows: rows, expectedRevision: head.revision ?? 1 },
  });
}

/**
 * One hidden turn per group of agent datasets: those made without connectors run connector-free,
 * the others with them. Each group saves on top of the version the previous one saved.
 */
async function runAgentRefreshTurns(
  panel: SavedDashboard,
  agentDatasets: DatasetDescriptor[],
  signal?: AbortSignal,
): Promise<void> {
  let instructed = panel;
  const connectorFree = agentDatasets.filter(isConnectorFreeDataset);
  const connectorUsing = agentDatasets.filter(
    (dataset) => !isConnectorFreeDataset(dataset),
  );
  const split = connectorFree.length > 0 && connectorUsing.length > 0;
  const groups = [
    { datasets: connectorFree, free: true },
    { datasets: connectorUsing, free: false },
  ].filter((group) => group.datasets.length > 0);
  for (const group of groups) {
    const names = split
      ? group.datasets.map((dataset) => String(dataset['name']))
      : undefined;
    const answer = await runHiddenDashboardTurn(
      panel.workspaceId,
      `Dashboard refresh: ${panel.name}`,
      buildHtmlRefreshInstruction(instructed, names),
      signal,
      group.free,
    );
    if (!answer?.saved) {
      throw new Error('The refresh did not update the data. Try again.');
    }
    instructed = { ...instructed, revision: answer.saved.revision };
  }
}

/** Whether an agent dataset was made without connectors: its recipe lists `sources` and the list is empty. */
function isConnectorFreeDataset(dataset: DatasetDescriptor): boolean {
  const sources = (recipeOfDataset(dataset) as { sources?: unknown } | null)
    ?.sources;
  return Array.isArray(sources) && sources.length === 0;
}

export async function reorderDashboards(
  ids: string[],
  workspaceId?: string | null,
): Promise<SavedDashboard[]> {
  const headers = await authHeaders();
  const body = workspaceId ? { ids, workspace_id: workspaceId } : { ids };
  const res = await fetch(`${ENDPOINT}/reorder`, {
    method: 'POST',
    headers,
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(text.trim() || `Reorder failed (${res.status})`);
  }
  return (await res.json()) as SavedDashboard[];
}

/** The list poll while one dashboard is open beside its assistant, where other people's edits arrive. */
const FAST_POLL_MS = 15_000;

/**
 * A saved dashboard can now be shared, so the gallery must pick up an author's refreshes and audience
 * changes by reading, not only on the viewer's own actions. `refetchOnWindowFocus` and a 60s poll cover
 * that; this is a viewer catching up, never a trigger — the viewer's own tab causes no refresh. Focus
 * refetches `'always'`, not `true`: `true` obeys `staleTime`, so coming back to the tab within 30s of
 * the last read would show the author's old rows, which is exactly when a viewer looks.
 * `refetchIntervalInBackground: false` stops the poll once the tab is hidden. `fastPoll` shortens the
 * poll to 15s and is for the detail page only, while it is mounted.
 */
export function dashboardsQueryOptions(
  workspaceId?: string | null,
  options: { fastPoll?: boolean } = {},
) {
  return queryOptions({
    queryKey: dashboardKeys.list(workspaceId),
    queryFn: () => fetchDashboards(workspaceId),
    enabled: workspaceId !== null,
    staleTime: 30_000,
    refetchOnWindowFocus: 'always',
    refetchInterval: options.fastPoll ? FAST_POLL_MS : 60_000,
    refetchIntervalInBackground: false,
  });
}

export function useCreateDashboard(): UseMutationResult<
  SavedDashboard,
  Error,
  CreateDashboardInput
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateDashboardInput) =>
      createDashboard(input, useUiStore.getState().selectedWorkspaceId),
    onSuccess: () => void qc.invalidateQueries({ queryKey: dashboardKeys.all }),
  });
}

export function useUpdateDashboard(): UseMutationResult<
  SavedDashboard,
  Error,
  { id: string; patch: DashboardPatch }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: updateDashboard,
    onSuccess: () => void qc.invalidateQueries({ queryKey: dashboardKeys.all }),
  });
}

export function useDeleteDashboard(): UseMutationResult<void, Error, string> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: deleteDashboard,
    onSuccess: () => void qc.invalidateQueries({ queryKey: dashboardKeys.all }),
  });
}

export function useRefreshDashboard(): UseMutationResult<
  SavedDashboard,
  Error,
  SavedDashboard
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (panel: SavedDashboard) => refreshDashboard(panel),
    onSuccess: () => void qc.invalidateQueries({ queryKey: dashboardKeys.all }),
  });
}

export function useReorderDashboards(): UseMutationResult<
  SavedDashboard[],
  Error,
  string[]
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ids: string[]) =>
      reorderDashboards(ids, useUiStore.getState().selectedWorkspaceId),
    // Optimistically apply the new order so the drag result sticks instantly; reconcile from the
    // server response, and roll back to the cached list on failure. The cache key is the current
    // workspace's list so the optimistic write lands on the exact query the gallery reads.
    onMutate: async (ids: string[]) => {
      const key = dashboardKeys.list(useUiStore.getState().selectedWorkspaceId);
      await qc.cancelQueries({ queryKey: key });
      const previous = qc.getQueryData<SavedDashboard[]>(key);
      if (previous) {
        const byId = new Map(previous.map((d) => [d.id, d]));
        const reordered = ids
          .map((id) => byId.get(id))
          .filter((d): d is SavedDashboard => !!d);
        qc.setQueryData(key, reordered);
      }
      return { previous, key };
    },
    onError: (_err, _ids, ctx) => {
      if (ctx?.previous) qc.setQueryData(ctx.key, ctx.previous);
    },
    onSuccess: (data, _ids, ctx) => qc.setQueryData(ctx.key, data),
  });
}

/**
 * Toggle a panel's collapsed flag with an optimistic cache flip so the accordion responds instantly.
 * Mirrors useReorderDashboards: apply the new value to the cached list immediately, PATCH, reconcile
 * from the server response, and roll back to the previous list on failure. A collapsed panel renders
 * header-only and stops refreshing — see DashboardsView.
 */
export function useSetDashboardCollapsed(): UseMutationResult<
  SavedDashboard,
  Error,
  { id: string; collapsed: boolean }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, collapsed }: { id: string; collapsed: boolean }) =>
      setDashboardLayout(id, { collapsed }),
    onMutate: async ({ id, collapsed }: { id: string; collapsed: boolean }) => {
      const key = dashboardKeys.list(useUiStore.getState().selectedWorkspaceId);
      await qc.cancelQueries({ queryKey: key });
      const previous = qc.getQueryData<SavedDashboard[]>(key);
      if (previous) {
        qc.setQueryData<SavedDashboard[]>(
          key,
          previous.map((dashboard) =>
            dashboard.id === id ? { ...dashboard, collapsed } : dashboard,
          ),
        );
      }
      return { previous, key };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.previous) qc.setQueryData(ctx.key, ctx.previous);
    },
    onSuccess: (updated: SavedDashboard, _vars, ctx) =>
      qc.setQueryData<SavedDashboard[]>(ctx.key, (list) =>
        list?.map((dashboard) =>
          dashboard.id === updated.id ? updated : dashboard,
        ),
      ),
  });
}
